import React from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { theme } from '../../themes/theme'
import { Button as MuiButton, CircularProgress, Fade } from '@mui/material'

const { text, primaryButton, secondaryButton } = theme.colors

const padding = {
  default: '10px 20px',
  short: '7px 20px',
  large: '13px 30px'
}

const ButtonSpinner = styled(CircularProgress)`
  position: absolute;
  margin-left: -12px;
  margin-top: -12px;
  left: 50%;
  top: 50%;
  color: ${theme.colors.primary};
`

const DefaultButton = styled(MuiButton)`
  display: flex;
  text-transform: none;
  align-items: center;
  justify-content: center;
  gap: 5px;

  border-radius: ${theme.borderRadius.button};
  background: ${primaryButton.background};
  border: 0.775px solid ${primaryButton.border};
  padding: ${props => padding[props.padding]};
  width: ${props => props.width};

  color: ${primaryButton.text};
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  line-height: 15px;

  cursor: pointer;

  a {
    text-decoration: none;
    color: ${primaryButton.text};
  }

  .MuiSvgIcon-root {
    height: 15px;
    width: 15px;
  }

  &.Mui-disabled {
    color: ${primaryButton.disabledText};
    background: ${primaryButton.disabled};
  }

  :hover:not([disabled]) {
    background: ${primaryButton.hover};
    color: ${primaryButton.hoverText};
  }

  :hover:disabled {
    cursor: default;
  }
`

const Button = ({ children, loading, padding = 'default', ...props }) => (
  <DefaultButton disabled={loading} {...props} padding={padding}>
    {loading && (
      <Fade
        in={loading}
        style={{
          transitionDelay: loading ? '800ms' : '0ms'
        }}
        unmountOnExit>
        <ButtonSpinner size={24} />
      </Fade>
    )}
    {children}
  </DefaultButton>
)

const SecondaryButton = styled(Button)`
  border: 0.775px solid ${secondaryButton.border};
  border-radius: ${theme.borderRadius.button};
  color: ${secondaryButton.text};
  background: ${secondaryButton.background};
  width: ${props => props.width};

  :hover:not([disabled]) {
    border: 0.775px solid ${secondaryButton.hoverBorder};
    background: ${secondaryButton.hover};
    color: ${secondaryButton.text};
  }

  :hover:disabled {
    background: ${secondaryButton.disabled};
    cursor: default;
  }

  :disabled {
    background: ${secondaryButton.disabled};
    color: ${secondaryButton.disabledText};
    opacity: 0.5;
  }
`

const DangerButton = styled(SecondaryButton)`
  color: ${theme.colors.error};
  border-color: ${theme.colors.error};
  width: fit-content;

  :hover:not([disabled]) {
    color: ${theme.colors.error};
    border-color: ${theme.colors.error};
    background: ${theme.colors.errorLight};
  }
`

const MenuButton = styled(SecondaryButton)`
  background: ${props => (props.active === 'true' ? theme.colors.primaryLight : 'transparent')};
  border: none;
  padding: 8px 15px;
  justify-content: flex-start;
  gap: 10px;
  color: ${text.body};

  :hover:not([disabled]) {
    border: none;
    background: ${theme.colors.primaryExtraLight};
    color: ${text.body};
  }

  .MuiSvgIcon-root {
    height: 18px;
    width: 18px;
  }
`

const IconButton = styled(Button)`
  padding: 6px;
  border-radius: ${theme.borderRadius.iconButton};
  min-width: auto;
`

const SecondaryIconButton = styled(SecondaryButton)`
  padding: 6px;
  border-radius: ${theme.borderRadius.iconButton};
  min-width: auto;
`

const IconOnlyButton = styled(IconButton)`
  background: none;
  border: none;
  color: ${secondaryButton.text};

  :hover:not([disabled]) {
    background: none;
    color: ${secondaryButton.text};
  }

  :disabled {
    background: none;
    color: ${secondaryButton.text};
    opacity: 0.5;
  }
`

const TextButton = styled(Button)`
  color: ${text.link};
  text-decoration: underline;
  border: none;
  background: transparent;
  padding: 0;

  :hover {
    background: transparent;
    text-decoration: underline;
    color: ${text.linkDisabled};
  }

  :hover:not([disabled]) {
    background: none;
    color: ${text.linkDisabled};
  }

  &.Mui-disabled {
    color: ${text.linkDisabled};
    background: none;
  }
`

const TableHeaderButton = styled(MuiButton)`
  padding: 0;
  color: ${props => (props.active === 'true' ? `${theme.colors.primary}` : `${theme.colors.text.body}`)};
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  text-transform: none;
  text-align: left;
  font-family: inherit;

  :hover {
    background: none;
  }

  svg {
    height: 20px;
    padding-top: 2px;
  }
`

Button.propTypes = {
  ...MuiButton.propTypes,
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  padding: PropTypes.oneOf(['default', 'short', 'large'])
}

export default Button
export { SecondaryButton, DangerButton, IconButton, SecondaryIconButton, IconOnlyButton, TextButton, MenuButton, TableHeaderButton }
